import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import SEOHead from './SEOHead';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Send,
  CheckCircle,
  Instagram,
  Linkedin,
  Facebook,
  MessageCircle,
  Headphones,
  Calendar,
  Loader2
} from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LfmuVksAAAAAKK19c2xryqRRtSGd4OeUaGDnEgC';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Get reCAPTCHA Enterprise token
      let captchaToken = null;
      if (window.grecaptcha && window.grecaptcha.enterprise) {
        await window.grecaptcha.enterprise.ready(async () => {});
        captchaToken = await window.grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, { action: 'CONTACT_FORM' });
      }

      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          company: formData.company || null,
          service: formData.service || null,
          message: formData.message,
          captcha_token: captchaToken
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', phone: '', company: '', service: '', message: '' });
      } else {
        setError(data.detail || 'Failed to send message. Please try again or contact us directly.');
      }
    } catch (err) {
      console.error('Contact form error:', err);
      setError('Failed to send message. Please try again or contact us directly at support@kingofit.ca');
    } finally {
      setIsLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      details: '647-951-3653',
      subtext: 'Mon-Fri 9am-6pm EST',
      action: 'tel:6479513653',
      actionText: 'Call Now'
    },
    {
      icon: Mail,
      title: 'Email',
      details: 'support@kingofit.ca',
      subtext: 'We reply within 24 hours',
      action: 'mailto:support@kingofit.ca',
      actionText: 'Send Email'
    },
    {
      icon: MapPin,
      title: 'Service Area',
      details: 'Greater Toronto Area',
      subtext: 'Toronto, Markham, Vaughan, Mississauga & more',
      action: null,
      actionText: null
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: 'Mon - Fri: 9am - 6pm',
      subtext: '24/7 Emergency Support Available',
      action: null,
      actionText: null
    }
  ];

  const services = [
    'IT Consulting & Support',
    'Remote Office Support',
    'Managed Services',
    'Backup & Disaster Recovery',
    'Cloud & Outsourced IT',
    'Hardware Solutions',
    'Cybersecurity',
    'Network Setup',
    'Other'
  ];

  const faqs = [
    {
      question: 'How quickly can you respond to IT emergencies?',
      answer: 'We offer 24/7 emergency support. For critical issues, we aim to respond within 30 minutes and have a technician working on your problem within 1-2 hours.'
    },
    {
      question: 'Do you offer on-site support?',
      answer: 'Yes! We provide both remote and on-site support throughout the Greater Toronto Area, including Toronto, Markham, Vaughan, Mississauga, Brampton, and surrounding areas.'
    },
    {
      question: 'What industries do you serve?',
      answer: 'We serve a wide range of industries including law firms, medical clinics, dental offices, accounting firms, manufacturing, retail, real estate, non-profits, and education.'
    },
    {
      question: 'Do you offer free consultations?',
      answer: 'Absolutely! We offer free initial consultations to understand your business needs and provide recommendations. There\'s no obligation to proceed.'
    }
  ];

  return (
    <>
      <SEOHead
        title="Contact King of IT | IT Support Toronto | Call 647-951-3653 | Free Consultation"
        description="Contact King of IT for IT support in Toronto. Call 647-951-3653 or email support@kingofit.ca. Free IT consultation, 24/7 emergency support, same-day service available across the GTA."
        path="/contact"
      />
      
      <main role="main">
        {/* Hero Section */}
        <section className="bg-gradient-navy text-white py-16 lg:py-20" aria-labelledby="contact-hero-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Badge className="mb-6 bg-primary/20 text-primary border-primary/30">
              Contact King of IT Toronto
            </Badge>
            <h1 id="contact-hero-heading" className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl xl:text-6xl mb-4 sm:mb-6">
              Get IT Support in <span className="text-gradient-gold">Toronto Today</span>
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-3xl mx-auto mb-6 sm:mb-8">
              Our team of IT experts is ready to help your Toronto business thrive. Whether you need computer repair, network setup, or managed IT services – contact us today for a <strong>free consultation</strong>. No obligation, just honest advice.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:6479513653" data-testid="contact-hero-phone">
                <Button size="lg" className="w-full sm:w-auto bg-gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90">
                  <Phone className="w-5 h-5 mr-2" />
                  647-951-3653
                </Button>
              </a>
              <a href="mailto:support@kingofit.ca" data-testid="contact-hero-email">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10">
                  <Mail className="w-5 h-5 mr-2" />
                  support@kingofit.ca
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
              {/* Contact Info */}
              <div className="space-y-6">
                <h2 className="font-display font-bold text-xl sm:text-2xl text-foreground mb-6 sm:mb-8">
                  Contact Information
                </h2>
                {contactInfo.map((info) => (
                  <Card key={info.title} className="p-4 sm:p-6 flex items-start gap-4 hover:shadow-card-hover transition-shadow">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-gold flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground text-sm sm:text-base">{info.title}</h3>
                      <p className="text-foreground text-sm sm:text-base">{info.details}</p>
                      <p className="text-xs sm:text-sm text-muted-foreground">{info.subtext}</p>
                      {info.action && (
                        <a 
                          href={info.action}
                          className="inline-flex items-center text-primary text-sm mt-2 hover:underline"
                        >
                          {info.actionText}
                        </a>
                      )}
                    </div>
                  </Card>
                ))}

                {/* Social Links */}
                <div className="pt-4 sm:pt-6">
                  <h3 className="font-semibold text-foreground mb-4">Follow Us</h3>
                  <div className="flex gap-3">
                    <a 
                      href="https://www.instagram.com/kingofit.ca/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-muted flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                    <a 
                      href="#" 
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-muted flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
                    >
                      <Linkedin className="w-5 h-5" />
                    </a>
                    <a 
                      href="#" 
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-muted flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
                    >
                      <Facebook className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="lg:col-span-2">
                <Card className="p-6 sm:p-8">
                  <h2 className="font-display font-bold text-xl sm:text-2xl text-foreground mb-2">
                    Send Us a Message
                  </h2>
                  <p className="text-muted-foreground mb-6 text-sm sm:text-base">
                    Fill out the form below and we&apos;ll get back to you within 24 hours.
                  </p>
                  
                  {submitted ? (
                    <div className="text-center py-8 sm:py-12">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                        <CheckCircle className="w-7 h-7 sm:w-8 sm:h-8 text-green-600" />
                      </div>
                      <h3 className="font-semibold text-lg sm:text-xl text-foreground mb-2">Message Sent!</h3>
                      <p className="text-muted-foreground">We&apos;ll get back to you within 24 hours.</p>
                      <Button 
                        onClick={() => setSubmitted(false)}
                        variant="outline"
                        className="mt-4"
                      >
                        Send Another Message
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                      {error && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                          {error}
                        </div>
                      )}
                      <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Your Name *
                          </label>
                          <Input
                            name="from_name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="John Doe"
                            required
                            className="w-full"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Email Address *
                          </label>
                          <Input
                            type="email"
                            name="from_email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            placeholder="john@company.com"
                            required
                            className="w-full"
                          />
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Phone Number
                          </label>
                          <Input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            placeholder="647-951-3653"
                            className="w-full"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Company Name
                          </label>
                          <Input
                            name="company"
                            value={formData.company}
                            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                            placeholder="Your Company"
                            className="w-full"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Service Interested In
                        </label>
                        <select
                          name="service"
                          value={formData.service}
                          onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                          className="w-full h-10 px-3 rounded-md border border-input bg-background text-foreground text-sm"
                        >
                          <option value="">Select a service...</option>
                          {services.map((service) => (
                            <option key={service} value={service}>{service}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Message *
                        </label>
                        <Textarea
                          name="message"
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          placeholder="Tell us about your IT needs, current challenges, or questions..."
                          required
                          rows={5}
                          className="w-full"
                        />
                      </div>
                      
                      {/* reCAPTCHA Enterprise badge info */}
                      <p className="text-xs text-gray-500 mb-4">
                        This site is protected by reCAPTCHA Enterprise and the Google{' '}
                        <a href="https://policies.google.com/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and{' '}
                        <a href="https://policies.google.com/terms" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Terms of Service</a> apply.
                      </p>
                      
                      <Button 
                        type="submit"
                        size="lg"
                        disabled={isLoading}
                        className="w-full sm:w-auto bg-gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90 disabled:opacity-50"
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5 mr-2" />
                            Send Message
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 lg:py-24 bg-muted/30">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10 lg:mb-12">
              <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
                FAQ
              </Badge>
              <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-foreground">
                Frequently Asked <span className="text-gradient-gold">Questions</span>
              </h2>
            </div>

            <div className="space-y-4 sm:space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index} className="p-4 sm:p-6">
                  <h3 className="font-semibold text-foreground mb-2 text-sm sm:text-base">{faq.question}</h3>
                  <p className="text-muted-foreground text-sm sm:text-base">{faq.answer}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Quick Contact Options */}
        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10 lg:mb-12">
              <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-foreground">
                Prefer Another Way to <span className="text-gradient-gold">Reach Us?</span>
              </h2>
            </div>

            <div className="grid sm:grid-cols-3 gap-6 lg:gap-8">
              <Card className="p-6 sm:p-8 text-center hover:shadow-card-hover transition-shadow">
                <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 rounded-full bg-gradient-gold flex items-center justify-center">
                  <Phone className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                </div>
                <h3 className="font-semibold text-lg sm:text-xl text-foreground mb-2">Call Us</h3>
                <p className="text-muted-foreground mb-4 text-sm sm:text-base">Speak directly with our team</p>
                <a href="tel:6479513653">
                  <Button className="w-full bg-gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90">
                    647-951-3653
                  </Button>
                </a>
              </Card>

              <Card className="p-6 sm:p-8 text-center hover:shadow-card-hover transition-shadow">
                <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 rounded-full bg-gradient-gold flex items-center justify-center">
                  <Calendar className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                </div>
                <h3 className="font-semibold text-lg sm:text-xl text-foreground mb-2">Book a Consultation</h3>
                <p className="text-muted-foreground mb-4 text-sm sm:text-base">Pick a day & time that works</p>
                <a 
                  href="https://outlook.office.com/bookwithme/user/20aee090aecf4040bdf81b47ab3eb662@kingofit.ca/meetingtype/53RXj902RESlZcG0PgiU7w2?anonymous&ismsaljsauthenabled&ep=mcard"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="w-full bg-gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90">
                    Schedule Now
                  </Button>
                </a>
              </Card>

              <Card className="p-6 sm:p-8 text-center hover:shadow-card-hover transition-shadow">
                <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 rounded-full bg-gradient-gold flex items-center justify-center">
                  <Headphones className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                </div>
                <h3 className="font-semibold text-lg sm:text-xl text-foreground mb-2">24/7 Support</h3>
                <p className="text-muted-foreground mb-4 text-sm sm:text-base">Emergency IT assistance</p>
                <a href="tel:6479513653">
                  <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary hover:text-white">
                    Get Support
                  </Button>
                </a>
              </Card>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-12 lg:py-16 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-secondary rounded-2xl h-48 sm:h-64 flex items-center justify-center">
              <div className="text-center text-white px-4">
                <MapPin className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 text-primary" />
                <p className="text-lg sm:text-xl font-semibold">Serving the Greater Toronto Area</p>
                <p className="text-muted-foreground text-sm sm:text-base">Toronto • Markham • Vaughan • Mississauga • Brampton • Richmond Hill</p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default ContactPage;
